import { ToolTracking } from "@jmc/core/src/components/Tracking";
import { useLocale } from "@jmc/core/src/hooks/useLocale";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Spinner } from "@jmc/solid-design-system/src/components/atoms/Spinner/Spinner";
import { parsePhoneNumber } from "@jmc/solid-design-system/src/components/molecules/PhoneNumberInput/PhoneNumberInput";
import { default as stripSlash } from "@jmc/utils/utils/strip-slash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import style from "./one-trust-webform-branded.module.scss";

interface WebFormProps {
    id: string;
    buttonId: string;
    thankYouUrl: string;
}

export const OneTrustWebFormBranded = ({ id, buttonId, thankYouUrl }: WebFormProps): JSX.Element => {
    const [loading, setLoading] = useState(true);
    let script;

    const scriptRef = useRef(null);
    const { t } = useTranslation();

    const shouldUseOneTrustProdEnv = process.env.GATSBY_CONSENT_ONETRUST_ENV
        ? process.env.GATSBY_CONSENT_ONETRUST_ENV !== "uat"
        : process.env.GATSBY_PRIVACY_POLICIES_ONETRUST_ENV !== "uat";

    const onetrustDomain = shouldUseOneTrustProdEnv
        ? "privacyportal-de.onetrust.com"
        : "privacyportaluatde.onetrust.com";
    const locale = useLocale();

    const cleanedOneTrustButtonId = [...buttonId.matchAll(/(?:ot-)?(.+)/g)];

    useEffect(() => {
        script = document.createElement("script");
        script.src = `https://${onetrustDomain}/consent-embedded-forms/consent-receipt-scripts/OtFormStub.js`;
        script.type = "text/javascript";
        script.setAttribute("dataId", id);
        scriptRef.current = script;
        document.head.appendChild(script);

        const errorDiv = document.createElement("div");
        errorDiv.setAttribute("class", "errorDiv");

        script.onload = function () {
            setTimeout(() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any)?.OneTrust?.webform?.InstallCP(cleanedOneTrustButtonId[0][1], {});
            }, 2000);

            setTimeout(() => {
                const phoneInput = document.querySelector(".ot-phone");
                const submitButton = document.querySelector(".ot-submit");

                phoneInput && !document.getElementsByClassName("errorDiv")[0] && phoneInput.appendChild(errorDiv);

                let countryCodeValue = "";
                let telInputValue = "";
                let phoneInputEnabled = false;

                const options = {
                    attributes: true,
                };

                function performValidation(): void {
                    if (phoneInputEnabled) {
                        validatePhonenumber();
                    }
                }

                function keyupEventHandler(): void {
                    performValidation();
                }

                function telInputEventHandler(e): void {
                    const checkChars = /[^0-9]/gi;
                    if (checkChars.test(e.target.value)) {
                        e.target.value = e.target.value.replace(checkChars, "");
                    }
                    telInputValue = e.target.value;
                    performValidation();
                }

                //Showing duplicate submit button to prevent the flickering of submit button caused by overriding the onetrust form validation
                function showDuplicateSubmitButton(): void {
                    submitButton.style.display = "none";
                    const dupButton = document.createElement("button");
                    dupButton.innerText = submitButton.innerHTML;
                    dupButton.setAttribute("class", "dupButton");
                    dupButton.disabled = true;
                    dupButton.ariaDisabled = "true";
                    const btnDiv = document.getElementsByClassName("ot-btns")[0];
                    const buttonClass = document.getElementsByClassName("dupButton")[0];
                    !buttonClass && btnDiv.appendChild(dupButton);
                }

                // removing duplicate submit buttopn and showing the original onetrust submit button
                function removeDuplicateSubmitButton(): void {
                    submitButton.style.display = "inline";
                    document.getElementsByClassName("dupButton")[0] &&
                        document.getElementsByClassName("dupButton")[0].remove();
                }

                function validatePhonenumber(): void {
                    //validating phone number using libphonenumber-js
                    const fullNumber = `${countryCodeValue}${telInputValue || ""}`;
                    const parsedNumber = parsePhoneNumber(fullNumber);

                    if (!parsedNumber || !parsedNumber.valid) {
                        showDuplicateSubmitButton();
                        document.getElementsByClassName("errorDiv")[0].innerHTML = t(
                            "common:Please provide a valid phone number",
                        );
                    } else {
                        removeDuplicateSubmitButton();
                        document.getElementsByClassName("errorDiv")[0].innerHTML = "";
                    }
                }

                function callback(mutationList): void {
                    mutationList.forEach(function (mutation) {
                        if (mutation.type === "attributes" && mutation.attributeName === "class") {
                            const phoneCheckBoxUnchecked = mutation.target.classList.contains("ot-n");
                            phoneInputEnabled = false;
                            removeDuplicateSubmitButton();
                            if (!phoneCheckBoxUnchecked) {
                                phoneInputEnabled = true;
                                showDuplicateSubmitButton();
                                const telInput = document.querySelector('input[type="tel"]');
                                telInput.addEventListener("keyup", telInputEventHandler);
                                const countryCode = document.querySelector(".ot_cc_list");
                                telInputValue = telInput.value;
                                countryCodeValue = countryCode.value;
                                validatePhonenumber();
                            }
                        }
                    });
                }

                if (phoneInput) {
                    const selects = document.querySelectorAll("select");
                    selects.forEach(function (select) {
                        select.onchange = function () {
                            performValidation();
                        };
                    });

                    const inputElements = document.querySelectorAll('input[type="text"]');
                    for (const el of inputElements) {
                        el.addEventListener("keyup", keyupEventHandler);
                    }

                    const observer = new MutationObserver(callback);
                    observer.observe(phoneInput, options);
                }
            }, 5000);
        };

        setLoading(false);

        return () => {
            if (scriptRef.current) {
                document.head.removeChild(scriptRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const redirect = (): void => {
            setTimeout(() => {
                window.location.assign(
                    `${stripSlash.stripTrailing(process.env.GATSBY_APP_DOMAIN)}/${stripSlash.strip(
                        locale,
                    )}/${stripSlash.strip(thankYouUrl)}`,
                );
            }, 1000);
        };
        window.addEventListener("OneTrustWebFormSubmitted", redirect);

        return () => {
            window.removeEventListener("OneTrustWebFormSubmitted", redirect);
        };
    }, [locale, thankYouUrl]);

    return (
        <div id="onetrustwebform" className={style["onetrust-webform"]}>
            {loading && <Spinner />}
            {!loading && (
                <ToolTracking
                    eventType={EventTypes.TOOL_STEP}
                    name="embedded_form"
                    type="onetrust_consent"
                    stepnumber="0"
                    stepname="stay_in_touch"
                    targeturl={thankYouUrl}
                />
            )}
            <div id="ot-container"></div>
        </div>
    );
};

export default OneTrustWebFormBranded;
